import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { FormField, Input, Select, Textarea, SubmitBtn } from '../components/FormField';

const emptyForm = {
  name:'', email:'', username:'', password:'', phone:'',
  class_id:'', student_number:'', parent_name:'', parent_phone:'',
  birth_date:'', gender:'', address:''
};

export default function Students() {
  const { t } = useLanguage();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [section, setSection] = useState('all');
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const sectionOf = (s) => {
    if (s.class_section) return s.class_section;
    if (s.enrollment_section) return s.enrollment_section;
    const g = String(s.enrollment_grade || s.class_name || '').toLowerCase();
    if (/nursery|reception|transition|pre.?nursery/.test(g)) return 'preschool';
    const m = g.match(/^(\d+)/);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n >= 1 && n <= 5) return 'primary';
      if (n >= 6 && n <= 12) return 'secondary';
    }
    return null;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([API.get('/students'), API.get('/classes')]);
      setStudents(s.data || []);
      setClasses(c.data || []);
    } catch (err) {
      console.error('students load failed:', err.response?.status, err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(emptyForm); setError(''); setModal('add'); };
  const openEdit = row => {
    // birth_date API-dən ISO kimi gəlir; state-də yalnız YYYY-MM-DD saxlanılır,
    // yoxsa tarixə toxunulmadan saxlayanda MySQL-ə ISO gedir və xəta verir
    setForm({ ...emptyForm, ...row, birth_date: (row.birth_date || '').split('T')[0], password: '' });
    setError(''); setModal('edit');
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (modal === 'add') await API.post('/students', form);
      else await API.put(`/students/${form.id}`, form);
      setModal(null); load();
    } catch (err) {
      setError(err.response?.data?.message || t('students.error'));
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm(t('students.confirmDelete'))) return;
    try {
      await API.delete(`/students/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.error || 'Delete failed');
    }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Məlumat qəbul müraciətindən gəlir; burada yalnız göstərilir.
  // Düzəliş "Şagird Qeydiyyatı -> Qəbul Müraciətləri" bölməsində aparılır,
  // beləliklə eyni şagird iki yerdə fərqli yazılmır.
  const nameParts = (r) => {
    if (r.adm_first_name || r.adm_last_name) {
      return { first: r.adm_first_name || '', last: r.adm_last_name || '' };
    }
    const p = String(r.name || '').trim().split(/\s+/);
    return { first: p.slice(0, -1).join(' ') || p[0] || '', last: p.length > 1 ? p[p.length - 1] : '' };
  };
  const genderLabel = (r) => {
    const g = r.adm_gender || r.gender;
    if (g === 'male')   return t('students.genderMale');
    if (g === 'female') return t('students.genderFemale');
    return '—';
  };

  const columns = [
    { header: '#', render: (_, i) => i + 1, width: '48px' },
    { header: t('students.colFirstName'),  render: r => nameParts(r).first || '—' },
    { header: t('students.colLastName'),   render: r => nameParts(r).last  || '—' },
    { header: t('students.colFatherName'), render: r => r.adm_father_name || '—' },
    { header: t('students.colClass'),      render: r => r.class_name || r.enrollment_grade || '—' },
    { header: t('students.colGender'),     render: r => genderLabel(r) },
  ];

  return (
    <div>
      <PageHeader icon="students" title={t('students.titleEmoji')} subtitle={`${t('common.total')} ${students.length} ${t('students.countSuffix')}`}
        />


      {(() => {
        const counts = { all: students.length, preschool: 0, primary: 0, secondary: 0, none: 0 };
        students.forEach(s => { const sec = sectionOf(s); counts[sec || 'none']++; });
        const TABS = [
          { v: 'all',       label: t('common.all'),                    color: '#0f172a' },
          { v: 'preschool', label: t('classes.sectionPreschool'),      color: '#0891b2' },
          { v: 'primary',   label: t('classes.sectionPrimary'),        color: '#059669' },
          { v: 'secondary', label: t('classes.sectionSecondary'),      color: '#7c3aed' },
        ];
        return (
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14}}>
            {TABS.map(tab => (
              <button key={tab.v} onClick={() => setSection(tab.v)}
                style={{
                  padding:'10px 18px',borderRadius:12,border:'1.5px solid '+(section===tab.v?tab.color:'#e2e8f0'),
                  background: section===tab.v ? tab.color : '#fff',
                  color: section===tab.v ? '#fff' : '#475569',
                  fontWeight:700,fontSize:'0.86rem',cursor:'pointer',
                  display:'inline-flex',alignItems:'center',gap:8,
                  transition:'all .15s',
                  boxShadow: section===tab.v ? '0 4px 12px '+tab.color+'33' : 'none',
                }}>
                {tab.label}
                <span style={{
                  background: section===tab.v ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
                  color: section===tab.v ? '#fff' : '#64748b',
                  fontSize:'0.74rem',padding:'2px 9px',borderRadius:999,fontWeight:800,
                  fontFeatureSettings: '"tnum"'
                }}>{counts[tab.v]}</span>
              </button>
            ))}
          </div>
        );
      })()}

      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        <input type="text" placeholder={t('common.searchPlaceholder')}
          value={search} onChange={e => setSearch(e.target.value)}
          style={{padding:'9px 14px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:'0.88rem',flex:1,minWidth:220,maxWidth:320}} />
        <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
          style={{padding:'9px 14px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:'0.85rem',color:'#374151',background:'#fff'}}>
          <option value="">{t('students.fieldClass')}: {t('common.all')}</option>
          {classes.filter(c => section === 'all' || (c.section || '').toLowerCase() === section).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select value={filterGender} onChange={e => setFilterGender(e.target.value)}
          style={{padding:'9px 14px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:'0.85rem',color:'#374151',background:'#fff'}}>
          <option value="">{t('students.fieldGender')}: {t('common.all')}</option>
          <option value="male">{t('students.genderBoy')}</option>
          <option value="female">{t('students.genderGirl')}</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{padding:'9px 14px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:'0.85rem',color:'#374151',background:'#fff'}}>
          <option value="">{t('common.status')}: {t('common.all')}</option>
          <option value="1">{t('common.active')}</option>
          <option value="0">{t('common.inactive')}</option>
        </select>
        {(search || filterClass || filterGender || filterStatus) && (
          <button onClick={() => { setSearch(''); setFilterClass(''); setFilterGender(''); setFilterStatus(''); }}
            style={{background:'#fee2e2',color:'#b91c1c',border:'none',borderRadius:10,padding:'9px 14px',fontSize:'0.85rem',fontWeight:700,cursor:'pointer'}}>
            {t('common.clear')}
          </button>
        )}
      </div>

      {(() => {
        const q = search.toLowerCase();
        const filtered = students.filter(s => {
          if (section !== 'all' && sectionOf(s) !== section) return false;
          if (filterClass && String(s.class_id) !== String(filterClass)) return false;
          if (filterGender && s.gender !== filterGender) return false;
          if (filterStatus !== '' && String(s.is_active ? 1 : 0) !== filterStatus) return false;
          if (q) {
            const hay = ((s.name || '') + ' ' + (s.username || '') + ' ' + (s.student_number || '') + ' ' + (s.parent_name || '')).toLowerCase();
            if (!hay.includes(q)) return false;
          }
          return true;
        });
        return <DataTable columns={columns} data={filtered} loading={loading} emptyMsg={t('students.emptyMsg')} />;
      })()}

      {modal && (
        <Modal title={modal === 'add' ? t('students.modalNew') : t('students.modalEdit')} onClose={() => setModal(null)} size="lg">
          <form onSubmit={handleSave}>
            {error && <div className="form-error-box">{error}</div>}
            <div className="form-row">
              <FormField label={t('common.fullName')} required><Input value={form.name} onChange={e=>f('name',e.target.value)} required /></FormField>
              <FormField label={t('common.username')} required><Input value={form.username} onChange={e=>f('username',e.target.value)} required /></FormField>
            </div>
            <div className="form-row">
              <FormField label={t('common.email')}><Input type="email" autoComplete="off" name="contact-email" value={form.email} onChange={e=>f('email',e.target.value)} /></FormField>
              <FormField label={modal==='add'?t('students.fieldPasswordHint'):t('students.fieldPasswordChange')}>
                <Input type="password" autoComplete="new-password" name="new-pass" value={form.password} onChange={e=>f('password',e.target.value)} placeholder={modal==='edit'?t('students.fieldPasswordPlaceholder'):''} />
              </FormField>
            </div>
            <div className="form-row">
              <FormField label={t('students.fieldClass')}>
                <Select value={form.class_id} onChange={e=>f('class_id',e.target.value)}>
                  <option value="">{t('students.selectClass')}</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </FormField>
              <FormField label={t('students.fieldStudentNumber')}><Input value={form.student_number} onChange={e=>f('student_number',e.target.value)} /></FormField>
            </div>
            <div className="form-row">
              <FormField label={t('students.fieldParentName')}><Input value={form.parent_name} onChange={e=>f('parent_name',e.target.value)} /></FormField>
              <FormField label={t('students.fieldParentPhone')}><Input value={form.parent_phone} onChange={e=>f('parent_phone',e.target.value)} /></FormField>
            </div>
            <div className="form-row">
              <FormField label={t('students.fieldBirthDate')}><Input type="date" value={form.birth_date || ''} onChange={e=>f('birth_date',e.target.value)} /></FormField>
              <FormField label={t('students.fieldGender')}>
                <Select value={form.gender} onChange={e=>f('gender',e.target.value)}>
                  <option value="">{t('common.select')}</option>
                  <option value="male">{t('students.genderBoy')}</option>
                  <option value="female">{t('students.genderGirl')}</option>
                </Select>
              </FormField>
            </div>
            <FormField label={t('students.fieldAddress')}><Textarea value={form.address} onChange={e=>f('address',e.target.value)} /></FormField>
            {modal==='edit' && (
              <FormField label={t('common.status')}>
                <Select value={form.is_active} onChange={e=>f('is_active',e.target.value)}>
                  <option value={1}>{t('common.active')}</option>
                  <option value={0}>{t('common.inactive')}</option>
                </Select>
              </FormField>
            )}
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={()=>setModal(null)}>{t('common.cancel')}</button>
              <SubmitBtn loading={saving}>{modal==='add'?t('common.add'):t('common.save')}</SubmitBtn>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
